import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  DownloadCloud,
  ExternalLink,
  GitBranch,
  History,
  Loader2,
  UploadCloud,
} from 'lucide-react';
import { useEffect, type ReactNode, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MarkdownText } from '@/components/chat/MarkdownText';
import { useIsInDraftMode } from '@/hooks/useDraftMode';
import { useImportHistory } from '@/hooks/useGit';
import {
  useDevelopCommits,
  useDevelopInfo,
  useGitJobStatus,
  usePullDevelop,
  usePullProd,
  usePushDevelop,
} from '@/hooks/useGit';
import { TERMINAL_JOB_STATUSES, type DevelopCommit } from '@/services/git.service';
import SourceRepoCard from './SourceRepoCard';
import { toastService } from '@/services/toast.service';

type GitOp = 'push' | 'pull';

/**
 * "Develop" names two different things here — Pliable's Develop branch and the
 * git branch Pliable imports from — and the old copy used it for both, so "Push
 * Develop to git" read as moving a branch to itself. Every string now says which
 * side it means: **Pliable** for the platform, **git** (and the branch by name)
 * for the repository.
 *
 * `branch` is the tenant's configured import branch, not a hardcoded "develop":
 * a customer-owned repo usually imports from `main`.
 */
const OP_COPY: Record<
  GitOp,
  {
    confirmTitle: string;
    confirmBody: (repo: string | null, branch: string) => ReactNode;
    running: string;
    runningBody: string;
    successTitle: string;
    successBody: string;
    successToast: string;
    successToastBody: string;
  }
> = {
  push: {
    confirmTitle: 'Push Pliable changes to git?',
    confirmBody: (repo, branch) => (
      <>
        This exports what is currently in Pliable and pushes it onto the{' '}
        <span className="font-mono">{branch}</span> branch of {repo}. Existing commits are preserved.
      </>
    ),
    running: 'Pushing Pliable changes to git…',
    runningBody: 'Exporting from Pliable and pushing to the repository…',
    successTitle: 'Pushed to git',
    successBody: 'Pull the branch locally and rebase your work onto it before syncing again.',
    successToast: 'Pliable changes pushed to git',
    successToastBody: 'Pull the branch locally and rebase onto it before syncing again.',
  },
  pull: {
    confirmTitle: 'Pull git changes into Pliable?',
    confirmBody: (repo, branch) => (
      <>
        This imports the <span className="font-mono">{branch}</span> branch of {repo} into the
        Pliable branch you are in — the same import the push webhook runs. Use it for the first
        import, or when a push or merge did not come through.
      </>
    ),
    running: 'Pulling git changes into Pliable…',
    runningBody: 'Cloning the repository and importing its models into Pliable…',
    successTitle: 'Pulled into Pliable',
    successBody: 'Pliable now reflects the repository.',
    successToast: 'Git changes pulled into Pliable',
    successToastBody: 'Pliable now reflects the repository.',
  },
};

/**
 * Shown in both modes.
 *
 * It used to be Develop-only, and that was right when git backed Develop and
 * nothing else. Now a repo can map a production branch too, so Production has the
 * same question to answer — is what is live the same as what is on that branch —
 * and the same recovery when a merge did not import. Repository settings were
 * never mode-specific at all; they are tenant config.
 *
 * What differs by mode is the ACTIONS. Develop can push and pull, because
 * exporting Pliable's state is a Develop operation (`export_project` asserts it).
 * Production can only pull: it is reached by publishing, and this is the manual
 * re-run of the import a merge should have triggered.
 */
export default function GitTab() {
  return <GitSettings />;
}

function GitSettings() {
  const queryClient = useQueryClient();
  const isInDraftMode = useIsInDraftMode();
  const { data: develop, isLoading } = useDevelopInfo();
  const pushMutation = usePushDevelop();
  const pullMutation = usePullDevelop();
  const pullProdMutation = usePullProd();
  const [confirmOp, setConfirmOp] = useState<GitOp | null>(null);
  const [activeOp, setActiveOp] = useState<GitOp | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const { data: jobStatus } = useGitJobStatus(jobId);

  const status = jobStatus?.job_status;
  const isTerminal = !!status && TERMINAL_JOB_STATUSES.has(status);
  const isRunning = !!jobId && !isTerminal;
  const succeeded = status === 'finished';
  const repo = develop?.repo ?? null;
  const copy = activeOp ? OP_COPY[activeOp] : null;
  const inSync = develop?.in_sync === true;

  useEffect(() => {
    if (!jobId || !isTerminal || !copy) return;
    // Refresh the displayed SHAs / sync status once the job lands. The commit list
    // is a separate key — `['git-develop']` does not prefix-match
    // `['git-develop-commits']` — and a push adds a commit to it.
    queryClient.invalidateQueries({ queryKey: ['git-develop'] });
    queryClient.invalidateQueries({ queryKey: ['git-develop-commits'] });
    if (succeeded) {
      toastService.success(copy.successToast, { description: copy.successToastBody });
    } else {
      toastService.error('Job failed', {
        description: jobStatus?.error_message || 'The git job failed.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isTerminal]);

  const start = async (op: GitOp) => {
    setConfirmOp(null);
    setActiveOp(op);
    try {
      const mutation =
        op === 'push' ? pushMutation : isInDraftMode ? pullMutation : pullProdMutation;
      const res = await mutation.mutateAsync();
      setJobId(res.job_id);
    } catch (e) {
      setActiveOp(null);
      toastService.error('Could not start job', {
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <>
      <SourceRepoCard />

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" /> Sync with git
          </CardTitle>
          <CardDescription>
            Move changes between Pliable and the repository. Push writes what is in Pliable onto the
            branch; pull imports the branch back into Pliable.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !repo ? (
            <p className="text-sm text-muted-foreground">
              No repository is connected yet. Connect one above, or run{' '}
              <span className="font-mono">plb login</span> to have Pliable provision one.
            </p>
          ) : develop?.mapped === false ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-mono">{repo}</span> has no production branch mapped, so
              Production is published inside Pliable rather than through git. Set one above to
              publish by merging.
            </p>
          ) : (
            <>
              <Row label="Repository" value={<span className="font-mono text-sm">{repo}</span>} />
              <Row
                label="Branch"
                value={
                  <a
                    href={develop?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    {develop?.branch ?? 'develop'} <ExternalLink className="h-3 w-3" />
                  </a>
                }
              />
              <Row
                label="Latest commit"
                value={
                  <span className="font-mono text-sm">
                    {develop?.sha ? develop.sha.slice(0, 7) : '—'}
                  </span>
                }
              />
              <Row
                label="Sync status"
                value={
                  inSync ? (
                    <Badge variant="outline" className="gap-1 border-green-600/40 text-green-700">
                      <CheckCircle2 className="h-3 w-3" /> In sync
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1 border-amber-600/40 text-amber-700">
                      <AlertCircle className="h-3 w-3" /> Out of sync
                    </Badge>
                  )
                }
              />
              {!inSync && (
                <p className="text-xs text-muted-foreground">
                  The branch has commits Pliable hasn&apos;t imported yet. Pull git changes into
                  Pliable to catch up (e.g. after a missed webhook).
                </p>
              )}
              <div className="flex gap-2 pt-2">
                {isInDraftMode && (
                  <Button onClick={() => setConfirmOp('push')} disabled={isRunning}>
                    <UploadCloud className="h-4 w-4 mr-2" /> Push Pliable Changes to Git
                  </Button>
                )}
                <Button
                  variant={isInDraftMode ? 'outline' : 'default'}
                  onClick={() => setConfirmOp('pull')}
                  disabled={isRunning}
                >
                  <DownloadCloud className="h-4 w-4 mr-2" /> Pull Git Changes into Pliable
                </Button>
              </div>
              {!isInDraftMode && (
                <p className="text-xs text-muted-foreground">
                  Production is updated by publishing. This re-runs the import for a merge that
                  did not come through — there is nothing to push from here.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {repo && <DevelopCommitsCard />}
      <ImportHistoryCard />

      <AlertDialog open={!!confirmOp} onOpenChange={(open) => !open && setConfirmOp(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmOp && OP_COPY[confirmOp].confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmOp && OP_COPY[confirmOp].confirmBody(repo, develop?.branch ?? 'develop')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConfirmOp(null)}>
              Cancel
            </Button>
            <Button onClick={() => confirmOp && void start(confirmOp)}>
              {confirmOp === 'pull' ? 'Pull' : 'Push'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!jobId}
        onOpenChange={(open) => {
          if (!open && !isRunning) {
            setJobId(null);
            setActiveOp(null);
          }
        }}
      >
        <DialogContent
          onPointerDownOutside={(e) => {
            if (isRunning) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isRunning) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {isRunning
                ? copy?.running
                : succeeded
                  ? copy?.successTitle
                  : 'Job failed'}
            </DialogTitle>
            <DialogDescription>
              {isRunning
                ? copy?.runningBody
                : succeeded
                  ? copy?.successBody
                  : jobStatus?.error_message || 'The git job failed.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-6">
            {isRunning ? (
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            ) : succeeded ? (
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            ) : (
              <AlertCircle className="text-destructive h-8 w-8" />
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setJobId(null);
                setActiveOp(null);
              }}
              disabled={isRunning}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      {value}
    </div>
  );
}

function DevelopCommitsCard() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useDevelopCommits(page);
  const commits = data?.commits ?? [];

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-4 w-4" /> Recent commits
        </CardTitle>
        <CardDescription>
          Commits on the develop branch, newest first — the message is the summary of what
          changed and why, written when the work was synced.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : commits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No commits on the develop branch yet.</p>
        ) : (
          <>
            <ul className="divide-y divide-border">
              {commits.map((c) => (
                <CommitRow key={c.sha} commit={c} />
              ))}
            </ul>
            <div className="flex items-center justify-between pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Newer
              </Button>
              <span className="text-xs text-muted-foreground">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!data?.has_more || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Older
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CommitRow({ commit }: { commit: DevelopCommit }) {
  const [expanded, setExpanded] = useState(false);
  const lines = commit.message.split('\n');
  const subject = lines[0] || '(no message)';
  const hasBody = lines.slice(1).some((l) => l.trim().length > 0);

  return (
    <li className="py-2">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => hasBody && setExpanded((v) => !v)}
          className="mt-0.5 text-muted-foreground hover:text-foreground disabled:opacity-0"
          disabled={!hasBody}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium">{subject}</span>
            <a
              href={commit.url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 font-mono text-xs text-primary hover:underline"
            >
              {commit.short_sha}
            </a>
          </div>
          <div className="text-xs text-muted-foreground">
            {commit.author ?? 'unknown'}
            {commit.date ? ` · ${new Date(commit.date).toLocaleString()}` : ''}
          </div>
          {expanded && hasBody && (
            <div className="mt-2 rounded-md border border-border bg-muted/30 p-3">
              <MarkdownText content={commit.message} />
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Every attempt to import the project from git, win or lose.
 *
 * The history exists because a webhook import fails on a worker with nobody
 * watching: RQ marks the job failed and that is the end of it, so the only way
 * to answer "did that push land?" was to read the logs. The banner shows the
 * newest failure; this shows whether the one before it worked, and what a
 * partial success actually left undone.
 */
function ImportHistoryCard() {
  const { data, isLoading } = useImportHistory();
  const runs = data?.imports ?? [];

  if (!isLoading && runs.length === 0) return null;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-4 w-4" /> Import history
        </CardTitle>
        <CardDescription>
          What Pliable has read from the repository, most recent first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <ul className="divide-y">
            {runs.map((run) => (
              <li key={run.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-start gap-3">
                  <ImportStatusBadge status={run.status} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">{run.summary}</div>
                    <div className="text-xs text-muted-foreground">
                      {run.trigger === 'WEBHOOK' ? 'a push to git' : run.trigger.toLowerCase()}
                      {run.commit_sha ? ` · ${run.commit_sha.slice(0, 7)}` : ''}
                      {run.started_at ? ` · ${new Date(run.started_at).toLocaleString()}` : ''}
                    </div>
                    {(run.error_message || run.skipped_reason) && (
                      <div className="mt-1 text-xs text-muted-foreground break-words">
                        {run.error_message || run.skipped_reason}
                      </div>
                    )}
                    {run.warnings.length > 0 && (
                      <ul className="mt-1 list-disc pl-4 text-xs text-amber-700 dark:text-amber-400">
                        {run.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ImportStatusBadge({ status }: { status: string }) {
  if (status === 'SUCCESS') return <Badge variant="secondary">Imported</Badge>;
  if (status === 'FAILED') return <Badge variant="destructive">Failed</Badge>;
  if (status === 'SKIPPED') return <Badge variant="outline">Not imported</Badge>;
  return <Badge variant="outline">Running</Badge>;
}
